import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ApiCallLog } from './api-call-log.model';
import { ApiCallLogsService } from './api-call-logs.service';
import { PagedEntities } from '@app/shared/abstract/paged-entities';
import { DataTableComponent } from '@app/partials/datatable/data-table/data-table.component';
import { Column } from '@app/partials/datatable/data-table/column';
import { RouteParams } from '@app/shared/abstract/route-params';
import { Subject, debounceTime } from 'rxjs';
import { SelectComponent } from '@app/partials/form/select/select.component';

@Component({
  selector: 'app-api-call-logs-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, DataTableComponent, SelectComponent],
  templateUrl: './api-call-logs-page.component.html',
})
export class ApiCallLogsPageComponent implements OnInit {
  dataPage?: PagedEntities<ApiCallLog>;
  selectedLog?: ApiCallLog;
  loading = false;
  copyNotice = false;
  tabPayload: unknown = null;
  private copyNoticeTimeout?: ReturnType<typeof setTimeout>;

  activeTab: LogTabId = 'response-body';
  readonly tabs: LogTab[] = [
    { id: 'request-headers', label: 'Req Headers', getValue: (log) => log.request_headers },
    { id: 'request-query', label: 'Req Query', getValue: (log) => log.request_query },
    { id: 'request-body', label: 'Req Body', getValue: (log) => log.request_body },
    { id: 'response-body', label: 'Res Body', getValue: (log) => log.response_body },
    { id: 'response-error', label: 'Error', getValue: (log) => ({ code: log.error_code, message: log.error_message }) },
    { id: 'token-claims', label: 'Token', getValue: (log) => log.token_claims },
  ];

  columns: Column<ApiCallLog>[] = [
    {
      field: 'created_at',
      header: 'When',
      type: 'date',
      dateFormat: 'en-GB',
      sortable: true,
      callback: (row) => this.formatTimestampShort(row.created_at),
    },
    { field: 'ip', header: 'IP', callback: (row) => row.ip ?? '—' },
    {
      field: 'status_code',
      header: 'Status',
      sortable: true,
      getColor: (row) => this.statusColor(row.status_code),
    },
    { field: 'method', header: 'Method', sortable: true },
    { field: 'path', header: 'Path' },
    {
      field: 'metrics',
      header: 'Duration',
      callback: (row) => (row.metrics?.duration_ms ? `${row.metrics.duration_ms.toFixed(2)} ms` : '—'),
    },
  ];

  methods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
  statusCodes = [200, 201, 204, 400, 401, 403, 404, 422, 429, 500];
  routeNames = [
    'venue-quote.config.years',
    'venue-quote.products',
    'venue-quote.dates.search',
    'venue-quote.calculate',
    'venue-quote.submit',
    'venue-quote.email-copy',
    'venue-quote.state',
    'api-call-logs.index',
    'api-call-logs.show',
  ];
  statusControl = new FormControl<number[]>([]);
  methodControl = new FormControl<string[]>([]);
  routeControl = new FormControl<string[]>([]);
  clientControl = new FormControl<string | undefined>(undefined);
  searchControl = new FormControl<string | undefined>(undefined);
  statusChoices: { value: any; label: string }[] = [];
  methodChoices: { value: any; label: string }[] = [];
  routeChoices: { value: any; label: string }[] = [];

  private filterChange$ = new Subject<void>();

  constructor(private service: ApiCallLogsService) {}

  ngOnInit(): void {
    this.statusChoices = this.statusCodes.map((c) => ({ value: c, label: c.toString() }));
    this.methodChoices = this.methods.map((m) => ({ value: m, label: m }));
    this.routeChoices = this.routeNames.map((r) => ({ value: r, label: r }));
    this.setupFilterDebounce();
    this.loadLogs();
  }

  private statusColor(code?: number | null): string {
    const val = code ?? 0;
    if (val >= 500) {
      if (val >= 520) return 'rose';
      if (val >= 510) return 'red';
      return 'orange';
    }
    if (val >= 400) {
      if (val >= 440) return 'amber';
      if (val >= 430) return 'yellow';
      if (val >= 420) return 'orange';
      return 'amber';
    }
    if (val >= 300) {
      if (val >= 308) return 'blue';
      return 'sky';
    }
    if (val === 204) return 'emerald';
    if (val === 201) return 'lime';
    if (val === 200) return 'green';
    if (val > 200 && val < 300) return 'emerald';
    return 'gray';
  }

  formatTimestampShort(value: string | Date | null | undefined): string {
    if (!value) {
      return '—';
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    const day = `${date.getDate()}`.padStart(2, '0');
    const month = date.toLocaleString('en-GB', { month: 'short' });
    const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

    return `${day} ${month} ${time}`;
  }

  selectTab(tabId: LogTabId): void {
    this.activeTab = tabId;
    this.tabPayload = this.getTabPayload(this.selectedLog);
  }

  getTabPayload(log: ApiCallLog | undefined): unknown {
    if (!log) {
      return null;
    }

    const tab = this.tabs.find((entry) => entry.id === this.activeTab);
    return tab ? tab.getValue(log) : null;
  }

  loadLogs(pageNum: number = 1, pageSize: number = 25): void {
    this.loading = true;
    this.service
      .getLogs({
        pageNum,
        pageSize,
        status_code: this.normalizeArrayFilter(this.statusControl.value),
        method: this.normalizeArrayFilter(this.methodControl.value),
        client_id: this.clientControl.value ?? undefined,
        route_name: this.normalizeArrayFilter(this.routeControl.value),
        q: this.searchControl.value ?? undefined,
      })
      .subscribe({
        next: (res) => {
          this.dataPage = res;
          if (!this.selectedLog && res.data.length > 0) {
            this.selectLog(res.data[0]);
          } else if (this.selectedLog) {
            const updated = res.data.find((l) => l.id === this.selectedLog?.id);
            this.selectedLog = updated ?? res.data[0];
          }
          this.tabPayload = this.getTabPayload(this.selectedLog);
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  selectLog(log: ApiCallLog): void {
    this.selectedLog = log;
    this.tabPayload = this.getTabPayload(log);
  }

  statusBadge(status?: number | null): string {
    if (!status) return 'badge';
    if (status >= 500) return 'badge error';
    if (status >= 400) return 'badge warning';
    if (status >= 200) return 'badge success';
    return 'badge';
  }

  refresh(): void {
    this.loadLogs(this.dataPage?.meta.current_page ?? 1, this.dataPage?.meta.per_page ?? 25);
  }

  handleRoute(params: RouteParams): void {
    this.loadLogs(params.pageNum, params.pageSize);
  }

  clearFilters(): void {
    this.statusControl.reset([]);
    this.methodControl.reset([]);
    this.routeControl.reset([]);
    this.clientControl.reset(undefined);
    this.searchControl.reset(undefined);
    this.loadLogs(1);
  }

  copyDetails(): void {
    if (!this.selectedLog) {
      return;
    }

    const log = this.selectedLog;
    const lines = [
      `Route: ${log.route_name ?? '—'}`,
      `Status: ${log.status_code ?? '—'}`,
      `Path: ${log.method} ${log.path}`,
      `IP: ${log.ip ?? '—'}`,
      `UA: ${log.user_agent ?? '—'}`,
      `Error: ${log.error_code ?? log.error_class ?? '—'}`,
      `Duration: ${log.metrics?.duration_ms ?? '—'} ms`,
      `Request headers: ${JSON.stringify(log.request_headers)}`,
      `Request query: ${JSON.stringify(log.request_query)}`,
      `Request body: ${JSON.stringify(log.request_body)}`,
      `Response body: ${JSON.stringify(log.response_body)}`,
      `Token claims: ${JSON.stringify(log.token_claims)}`,
    ];

    const text = lines.join('\n');
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => this.showCopyNotice()).catch(() => {});
    }
  }

  private showCopyNotice(): void {
    if (this.copyNoticeTimeout) {
      clearTimeout(this.copyNoticeTimeout);
    }
    this.copyNotice = true;
    this.copyNoticeTimeout = setTimeout(() => {
      this.copyNotice = false;
    }, 1500);
  }

  private setupFilterDebounce(): void {
    this.statusControl.valueChanges.subscribe(() => this.filterChange$.next());
    this.methodControl.valueChanges.subscribe(() => this.filterChange$.next());
    this.routeControl.valueChanges.subscribe(() => this.filterChange$.next());
    this.clientControl.valueChanges.subscribe(() => this.filterChange$.next());
    this.searchControl.valueChanges.subscribe(() => this.filterChange$.next());

    this.filterChange$.pipe(debounceTime(250)).subscribe(() => this.loadLogs(1));
  }

  private normalizeArrayFilter<T>(value: T[] | undefined | null): T[] | undefined {
    const cleaned = (value ?? []).filter((v) => v !== undefined && v !== null) as T[];
    return cleaned.length ? cleaned : undefined;
  }
}

type LogTabId =
  | 'request-headers'
  | 'request-query'
  | 'request-body'
  | 'response-body'
  | 'response-error'
  | 'token-claims';

type LogTab = {
  id: LogTabId;
  label: string;
  getValue: (log: ApiCallLog) => unknown;
};
